import { api } from '@/config/api';

export type OrderType = 'dine_in' | 'takeaway' | 'delivery';
export type OrderStatus = 'new' | 'preparing' | 'ready' | 'out_for_delivery' | 'completed' | 'cancelled';
export type OrderPaymentStatus = 'unpaid' | 'paid' | 'refunded';
export type OrderPaymentMethod = 'cash' | 'card' | 'online';

export interface OrderLine {
  lineUid: string;
  itemUid: string | null;
  name: string;
  unitPrice: number;
  quantity: number;
  notes: string | null;
  lineTotal: number;
}

export interface OrderTimelineEntry {
  action: 'placed' | 'status' | 'updated' | 'paid' | 'refunded' | 'cancelled' | 'invoiced' | 'notified';
  status: OrderStatus | null;
  note: string | null;
  at: string;
  byUserUid: string | null;
}

export interface Order {
  orderUid: string;
  number: number;
  type: OrderType;
  tableLabel: string | null;
  deliveryAddress: string | null;
  contactUid: string | null;
  customerName: string | null;
  customerPhone: string | null;
  items: OrderLine[];
  itemCount: number;
  subtotal: number;
  discount: number;
  taxRatePct: number;
  vatInclusive: boolean;
  taxAmount: number;
  total: number;
  currency: string;
  status: OrderStatus;
  paymentStatus: OrderPaymentStatus;
  paymentMethod: OrderPaymentMethod | null;
  paidAt: string | null;
  notes: string | null;
  placedAt: string;
  preparingAt: string | null;
  readyAt: string | null;
  outForDeliveryAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelledReason: string | null;
  invoiceDocumentUid: string | null;
  timeline: OrderTimelineEntry[];
  createdByUserUid: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface OrderBoard {
  timezone: string;
  dayStart: string;
  currency: string;
  counts: Record<'new' | 'preparing' | 'ready' | 'out_for_delivery' | 'completed_today' | 'cancelled_today', number>;
  today: { orders: number; revenue: number; unpaid: number; unpaidAmount: number; averageTicket: number };
  hasDelivery: boolean;
  active: Order[];
  completedToday: Order[];
}

export interface OrderPricing { currency: string; taxRatePct: number; vatInclusive: boolean; timezone: string }

export interface OrderLineInput {
  lineUid?: string;
  itemUid?: string;
  name?: string;
  unitPrice?: number;
  quantity: number;
  notes?: string;
}

export interface OrderInput {
  type?: OrderType;
  tableLabel?: string;
  deliveryAddress?: string;
  contactUid?: string;
  customerName?: string;
  customerPhone?: string;
  items?: OrderLineInput[];
  discount?: number;
  notes?: string;
  paymentMethod?: OrderPaymentMethod;
}

export interface OrderListParams {
  status?: string;
  type?: OrderType;
  paymentStatus?: OrderPaymentStatus;
  from?: string;
  to?: string;
  q?: string;
  page?: number;
  limit?: number;
}

const base = '/slighter_crm/orders';

export type OrderNotifyEvent = 'placed' | 'preparing' | 'ready' | 'out_for_delivery' | 'completed' | 'cancelled';
export interface OrderNotifyTemplate {
  templateUid: string;
  name: string;
  language: string;
  body: string;
  variables: Array<{ n: string; name: string | null }>;
  /** Why it can't carry order updates; null = usable. */
  problem: string | null;
}
export interface OrderNotifySettings {
  enabled: boolean;
  templateUid: string | null;
  events: OrderNotifyEvent[];
  availableEvents: OrderNotifyEvent[];
  whatsappConnected: boolean;
  templates: OrderNotifyTemplate[];
}

export const ordersService = {
  notifySettings: async (): Promise<OrderNotifySettings> => (await api.get(`${base}/notify-settings`)).data,
  updateNotifySettings: async (input: { enabled?: boolean; templateUid?: string | null; events?: OrderNotifyEvent[] }): Promise<OrderNotifySettings> =>
    (await api.put(`${base}/notify-settings`, input)).data,
  list: async (params: OrderListParams = {}): Promise<{ items: Order[]; total: number; page: number; limit: number }> =>
    (await api.get(base, { params })).data,
  board: async (): Promise<OrderBoard> => (await api.get(`${base}/board`)).data,
  pricing: async (): Promise<OrderPricing> => (await api.get(`${base}/pricing`)).data,
  get: async (orderUid: string): Promise<Order> => (await api.get(`${base}/${orderUid}`)).data,
  create: async (input: OrderInput): Promise<Order> => (await api.post(base, input)).data,
  update: async (orderUid: string, input: OrderInput): Promise<Order> => (await api.patch(`${base}/${orderUid}`, input)).data,
  setStatus: async (orderUid: string, status: Exclude<OrderStatus, 'cancelled'>): Promise<Order> =>
    (await api.post(`${base}/${orderUid}/status`, { status })).data,
  pay: async (orderUid: string, method: OrderPaymentMethod): Promise<Order> =>
    (await api.post(`${base}/${orderUid}/pay`, { method })).data,
  refund: async (orderUid: string): Promise<Order> => (await api.post(`${base}/${orderUid}/refund`)).data,
  cancel: async (orderUid: string, reason: string): Promise<Order> =>
    (await api.post(`${base}/${orderUid}/cancel`, { reason })).data,
  invoice: async (orderUid: string): Promise<{ documentUid: string; created: boolean; order: Order }> =>
    (await api.post(`${base}/${orderUid}/invoice`)).data,
};

export const ORDER_TYPE_LABEL: Record<OrderType, string> = { dine_in: 'Dine-in', takeaway: 'Takeaway', delivery: 'Delivery' };

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  new: 'New', preparing: 'Preparing', ready: 'Ready', out_for_delivery: 'Out for delivery', completed: 'Completed', cancelled: 'Cancelled',
};

/** The one-tap "move it along" step for the board. */
export function nextStatus(o: Pick<Order, 'status' | 'type'>): Exclude<OrderStatus, 'cancelled'> | null {
  switch (o.status) {
    case 'new': return 'preparing';
    case 'preparing': return 'ready';
    case 'ready': return o.type === 'delivery' ? 'out_for_delivery' : 'completed';
    case 'out_for_delivery': return 'completed';
    default: return null;
  }
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Preview of the server's arithmetic (per-line VAT, discount as a negative
 * line — the same as Documents). The placed order always carries the server's
 * own numbers.
 */
export function previewTotals(lines: Array<{ unitPrice: number; quantity: number }>, discount: number, taxRatePct: number, vatInclusive: boolean) {
  const subtotal = round2(lines.reduce((s, l) => s + round2(l.unitPrice * l.quantity), 0));
  const disc = round2(Math.min(Math.max(0, discount || 0), subtotal));
  const all = disc > 0 ? [...lines, { unitPrice: -disc, quantity: 1 }] : lines;
  let total = 0;
  let tax = 0;
  for (const l of all) {
    const sub = l.quantity * l.unitPrice;
    if (vatInclusive) {
      total = round2(total + sub);
      tax = round2(tax + (taxRatePct > 0 ? round2(sub - sub / (1 + taxRatePct / 100)) : 0));
    } else {
      const lineTax = round2(sub * (taxRatePct / 100));
      total = round2(total + round2(sub + lineTax));
      tax = round2(tax + lineTax);
    }
  }
  return { subtotal, discount: disc, taxAmount: tax, total };
}
